import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api'
import { Card, Button, Badge, Modal, Field, Input, Select } from './ui'
import { RowMenu } from './RowMenu'

interface JailStatus {
  jail: string; currently_failed: number; total_failed: number
  currently_banned: number; total_banned: number
}
interface BannedIP { jail: string; ip: string }

export function SecurityBlock({ nodeId }: { nodeId: string }) {
  const qc = useQueryClient()
  const [banOpen, setBanOpen] = useState(false)

  const { data: jails = [] } = useQuery({
    queryKey: ['f2b-jails', nodeId],
    queryFn: async () => (await api.get<JailStatus[]>(`/nodes/${nodeId}/f2b/jails`)).data,
    refetchInterval: 15000,
  })
  const { data: banned = [] } = useQuery({
    queryKey: ['f2b-banned', nodeId],
    queryFn: async () => (await api.get<BannedIP[]>(`/nodes/${nodeId}/f2b/banned`)).data,
    refetchInterval: 15000,
  })

  const unban = useMutation({
    mutationFn: async (b: BannedIP) => (await api.post(`/nodes/${nodeId}/f2b/unban`, b)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['f2b-banned', nodeId] }),
  })
  const installF2B = useMutation({
    mutationFn: async () => (await api.post(`/nodes/${nodeId}/f2b/install`)).data,
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['f2b-jails', nodeId] }), 15000),
  })

  const nothing = jails.length === 0

  return (
    <Card className="mb-5 overflow-hidden">
      <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold">Безопасность · Fail2ban</h2>
          <div className="text-[12px] text-neutral-500">Забаненные IP и активные jails</div>
        </div>
        {nothing
          ? <Button size="sm" onClick={() => installF2B.mutate()} disabled={installF2B.isPending}>
              {installF2B.isPending ? 'Ставим…' : 'Установить Fail2ban'}
            </Button>
          : <Button size="sm" variant="secondary" onClick={() => setBanOpen(true)}>Забанить IP</Button>}
      </div>

      {nothing ? (
        <div className="p-6 text-center text-neutral-400 text-[13px]">Fail2ban не установлен на этой ноде</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-neutral-100">
            {jails.map(j => (
              <div key={j.jail} className="p-3 border border-neutral-200 rounded-md bg-neutral-50/40">
                <div className="text-[11px] uppercase text-neutral-500 tracking-wider">{j.jail}</div>
                <div className="mt-1 text-[13px] flex items-center gap-2">
                  <b>{j.currently_banned}</b> сейчас
                  <span className="text-neutral-400 text-[11px]">· всего {j.total_banned}</span>
                </div>
              </div>
            ))}
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
              <tr>
                <th className="text-left font-medium px-5 py-2">IP-адрес</th>
                <th className="text-left font-medium px-4 py-2">Jail</th>
                <th className="text-right font-medium px-5 py-2 w-[80px]"></th>
              </tr>
            </thead>
            <tbody>
              {banned.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-6 text-center text-neutral-400">Никого не забанено</td></tr>
              )}
              {banned.map((b, i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                  <td className="px-5 py-2 font-mono">{b.ip}</td>
                  <td className="px-4 py-2"><Badge>{b.jail}</Badge></td>
                  <td className="px-5 py-2 text-right">
                    <RowMenu items={[
                      { label: 'Разбанить', onClick: () => unban.mutate(b) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {banOpen && <BanModal nodeId={nodeId} jails={jails.map(j => j.jail)}
        onClose={() => setBanOpen(false)}
        onDone={() => { setBanOpen(false); qc.invalidateQueries({ queryKey: ['f2b-banned', nodeId] }) }} />}
    </Card>
  )
}

function BanModal({ nodeId, jails, onClose, onDone }: {
  nodeId: string; jails: string[]; onClose: () => void; onDone: () => void
}) {
  const [f, setF] = useState({ jail: jails[0] || 'sshd', ip: '' })
  const ban = useMutation({
    mutationFn: async () => (await api.post(`/nodes/${nodeId}/f2b/ban`, f)).data,
    onSuccess: onDone,
  })
  return (
    <Modal open onClose={onClose} title="Забанить IP вручную">
      <form onSubmit={(e) => { e.preventDefault(); ban.mutate() }} className="space-y-4">
        <Field label="Jail">
          <Select value={f.jail} onChange={e => setF({ ...f, jail: e.target.value })}>
            {jails.map(j => <option key={j} value={j}>{j}</option>)}
          </Select>
        </Field>
        <Field label="IP-адрес">
          <Input value={f.ip} onChange={e => setF({ ...f, ip: e.target.value })} placeholder="1.2.3.4" required autoFocus className="font-mono" />
        </Field>
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={ban.isPending}>{ban.isPending ? '…' : 'Забанить'}</Button>
        </div>
      </form>
    </Modal>
  )
}
